import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from './context/ThemeContext';
import { fetchMapData, fetchMetadata, fetchPolesLiveStatus, fetchLocationsFilter } from './data/staticData';
import poleIcon from './assets/pole-icon.svg';


/**
 * Poles Table Component - Comprehensive table with search, filters, and sorting
 */
const PolesTable = ({ refreshTrigger }) => {
    const { theme } = useTheme();
    const [allPoles, setAllPoles] = useState(() => {
        try {
            const stored = localStorage.getItem('cached_poles_table_data');
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });
    const [loading, setLoading] = useState(() => {
        try {
            const stored = localStorage.getItem('cached_poles_table_data');
            return stored ? false : true;
        } catch {
            return true;
        }
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [zoneFilter, setZoneFilter] = useState('');
    const [wardFilter, setWardFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [aqiRangeFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const itemsPerPage = 10; //pagination poles in dashboard

    // Dynamic API data lists
    const [countriesList, setCountriesList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [sitesList, setSitesList] = useState([]);
    const [locationsList, setLocationsList] = useState([]);
    const [statusesList, setStatusesList] = useState([]);

    const countries = useMemo(() => {
        return countriesList.map(c => c.country_name).filter(Boolean);
    }, [countriesList]);

    const statuses = useMemo(() => {
        if (statusesList.length > 0) {
            return statusesList.map(s => s.description).filter(Boolean);
        }
        return [...new Set(allPoles.map(pole => pole.status).filter(Boolean))];
    }, [allPoles, statusesList]);

    const states = useMemo(() => {
        return statesList.map(s => s.state_name).filter(Boolean);
    }, [statesList]);

    const zones = useMemo(() => {
        return sitesList.map(s => s.site_name).filter(Boolean);
    }, [sitesList]);

    const wards = useMemo(() => {
        return locationsList.map(l => l.location_name).filter(Boolean);
    }, [locationsList]);

    // Initial load of countries and status data
    useEffect(() => {
        const loadInitialData = async () => {
            const res = await fetchLocationsFilter();
            if (res && res.status) {
                setCountriesList(res.data || []);
                if (res.statusdata) {
                    setStatusesList(res.statusdata);
                }
            }
        };
        loadInitialData();
    }, []);

    // Change handlers for dynamic filters cascading
    const handleCountryFilterChange = async (e) => {
        const val = e.target.value;
        setCountryFilter(val);
        setStateFilter('');
        setZoneFilter('');
        setWardFilter('');

        setStatesList([]);
        setSitesList([]);
        setLocationsList([]);

        if (!val) return;

        const selectedCountry = countriesList.find(c => c.country_name === val);
        if (selectedCountry) {
            const res = await fetchLocationsFilter({ country_id: selectedCountry.country_id });
            if (res && res.status) {
                setStatesList(res.data || []);
            }
        }
    };

    const handleStateFilterChange = async (e) => {
        const val = e.target.value;
        setStateFilter(val);
        setZoneFilter('');
        setWardFilter('');

        setSitesList([]);
        setLocationsList([]);

        if (!val) return;

        const selectedCountry = countriesList.find(c => c.country_name === countryFilter);
        const selectedState = statesList.find(s => s.state_name === val);
        if (selectedCountry && selectedState) {
            const res = await fetchLocationsFilter({
                country_id: selectedCountry.country_id,
                state_id: selectedState.state_id
            });
            if (res && res.status) {
                setSitesList(res.data || []);
            }
        }
    };

    const handleZoneFilterChange = async (e) => {
        const val = e.target.value;
        setZoneFilter(val);
        setWardFilter('');

        setLocationsList([]);

        if (!val) return;

        const selectedCountry = countriesList.find(c => c.country_name === countryFilter);
        const selectedState = statesList.find(s => s.state_name === stateFilter);
        const selectedSite = sitesList.find(s => s.site_name === val);
        if (selectedCountry && selectedState && selectedSite) {
            const res = await fetchLocationsFilter({
                country_id: selectedCountry.country_id,
                state_id: selectedState.state_id,
                site_id: selectedSite.site_id
            });
            if (res && res.status) {
                setLocationsList(res.data || []);
            }
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, countryFilter, stateFilter, zoneFilter, wardFilter, statusFilter, aqiRangeFilter]);

    useEffect(() => {
        const loadData = async (isFirstLoad = false) => {
            if (isFirstLoad) setLoading(true);
            try {
                // Fetch metadata and live status of all poles in parallel
                const [{ countries, sites, states }, result] = await Promise.all([
                    fetchMetadata(),
                    fetchPolesLiveStatus()
                ]);

                let rawData = [];
                let countVal = 0;
                if (result) {
                    if (Array.isArray(result)) {
                        rawData = result;
                        countVal = result.length;
                    } else if (result.data && Array.isArray(result.data)) {
                        rawData = result.data;
                        countVal = result.count || result.data.length;
                    }
                }

                if (rawData && Array.isArray(rawData)) {
                    const formattedData = rawData.map(item => {
                        const aqiBefore = item.aqiBefore?.AQI || 0;
                        const aqiAfter = item.aqiAfter?.AQI || 0;
                        let reductionCalc = 0;
                        if (aqiBefore > 0) {
                            reductionCalc = ((aqiBefore - aqiAfter) / aqiBefore) * 100;
                        }

                        const rawStatus = item.status_text || item.poleStatus || 'Offline';
                        const formattedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

                        // Hydrate country & state
                        let countryName = item.countryName || item.country || item.country_name || '';
                        let stateName = item.stateName || item.state || item.state_name || '';

                        if (!countryName || !stateName) {
                            const matchedSite = sites.find(s => s.site_name === item.siteName || s.site_name === item.site);
                            if (matchedSite) {
                                if (!countryName && matchedSite.site_country) {
                                    const cObj = countries.find(c => String(c.country_id) === String(matchedSite.site_country));
                                    if (cObj) countryName = cObj.country_name;
                                }
                                if (!stateName && matchedSite.site_state) {
                                    const sObj = states.find(s => String(s.state_id) === String(matchedSite.site_state));
                                    if (sObj) stateName = sObj.state_name;
                                }
                            }
                        }

                        let poleStatusId = item.poleStatusId;
                        if (poleStatusId == null && rawStatus) {
                            const st = rawStatus.toLowerCase();
                            if (st.includes('awaiting')) {
                                poleStatusId = 2;
                            } else if (st.includes('offline')) {
                                poleStatusId = 1;
                            } else if (st.includes('maintenance')) {
                                poleStatusId = 2;
                            } else {
                                poleStatusId = 3;
                            }
                        }

                        return {
                            ...item,
                            id: item.pole_id || item.poleNumber || '',
                            country: countryName || '',
                            state: stateName || '',
                            zone: item.site || item.siteName || '',
                            ward: item.location || item.locationName || '',
                            aqi: aqiBefore ? parseFloat(aqiBefore.toFixed(1)) : 0,
                            totalAirCleaned: item.total_air_cleaned_cuft_last_24h ?? item.dailyVolumeCubicFeet ?? 0,
                            dailyVolumeCubicFeet: item.total_air_cleaned_cuft_last_24h ?? item.dailyVolumeCubicFeet ?? 0,
                            reduction: reductionCalc ? parseFloat(reductionCalc.toFixed(1)) : 0,
                            status: formattedStatus,
                            poleStatusId: poleStatusId
                        };
                    });
                    setAllPoles(formattedData);
                    setTotalCount(countVal);
                    try {
                        localStorage.setItem('cached_poles_table_data', JSON.stringify(formattedData));
                    } catch (e) {
                        console.warn('Failed to cache poles table data:', e);
                    }
                }
            } catch (error) {
                console.error("Failed to load poles table data:", error);
            } finally {
                setLoading(false);
            }
        };

        const isFirstLoad = allPoles.length === 0;
        loadData(isFirstLoad);
    }, [refreshTrigger]);

    // Filter poles based on search and filters
    const filteredPoles = useMemo(() => {
        let filtered = allPoles;

        // Search filter
        if (searchTerm) {
            const query = searchTerm.toLowerCase();
            filtered = filtered.filter(pole =>
                String(pole.id || '').toLowerCase().includes(query) ||
                String(pole.country || '').toLowerCase().includes(query) ||
                String(pole.state || '').toLowerCase().includes(query) ||
                String(pole.zone || '').toLowerCase().includes(query) ||
                String(pole.ward || '').toLowerCase().includes(query) ||
                String(pole.status || '').toLowerCase().includes(query)
            );
        }

        // Country filter
        if (countryFilter) {
            filtered = filtered.filter(pole => pole.country === countryFilter);
        }

        // State filter
        if (stateFilter) {
            filtered = filtered.filter(pole => pole.state === stateFilter);
        }

        // Zone filter
        if (zoneFilter) {
            filtered = filtered.filter(pole => pole.zone === zoneFilter);
        }

        // Ward filter
        if (wardFilter) {
            filtered = filtered.filter(pole => pole.ward === wardFilter);
        }

        // Status filter
        if (statusFilter) {
            filtered = filtered.filter(pole => {
                const poleStatusLower = (pole.status || '').toLowerCase();
                const filterLower = statusFilter.toLowerCase();
                if (filterLower === 'online') {
                    return poleStatusLower.includes('online') || (!poleStatusLower.includes('offline') && !poleStatusLower.includes('awaiting'));
                }
                if (filterLower === 'offline') {
                    return poleStatusLower.includes('offline');
                }
                if (filterLower === 'Awaiting Connection') {
                    return poleStatusLower.includes('awaiting');
                }
                return pole.status === statusFilter;
            });
        }

        // AQI Range filter
        if (aqiRangeFilter) {
            filtered = filtered.filter(pole => {
                if (aqiRangeFilter === '0-50') return pole.aqi >= 0 && pole.aqi <= 50;
                if (aqiRangeFilter === '51-100') return pole.aqi >= 51 && pole.aqi <= 100;
                if (aqiRangeFilter === '101-150') return pole.aqi >= 101 && pole.aqi <= 150;
                if (aqiRangeFilter === '151-200') return pole.aqi >= 151 && pole.aqi <= 200;
                if (aqiRangeFilter === '201+') return pole.aqi >= 201;
                return true;
            });
        }

        return filtered;
    }, [allPoles, searchTerm, countryFilter, stateFilter, zoneFilter, wardFilter, statusFilter, aqiRangeFilter]);

    // Sort poles
    const sortedPoles = useMemo(() => {
        let sorted = [...filteredPoles];

        if (sortConfig.key) {
            sorted.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return sorted;
    }, [filteredPoles, sortConfig]);

    // Paginate
    const paginatedPoles = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedPoles.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedPoles, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedPoles.length / itemsPerPage);

    // Handle sorting
    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Get AQI status and color
    const getAQIStatus = (aqi) => {
        if (aqi <= 50) return { label: 'Good', color: 'good' };
        if (aqi <= 100) return { label: 'Moderate', color: 'moderate' };
        if (aqi <= 150) return { label: 'USG', color: 'unhealthy-sg' };
        if (aqi <= 200) return { label: 'Unhealthy', color: 'unhealthy' };
        if (aqi <= 300) return { label: 'Very Unhealthy', color: 'very-unhealthy' };
        return { label: 'Hazardous', color: 'hazardous' };
    };

    /*
    console.log('PolesTable State:', {
        allPolesLength: allPoles.length,
        totalCount,
        itemsPerPage,
        totalPages,
        currentPage,
        paginatedPolesLength: paginatedPoles.length,
        sortedPolesLength: sortedPoles.length
    });
    */

    return (
        <div className={`poles-table-section ${theme}`}>
            <div className="poles-table-header">
                <h2 className="section-title">Poles List + Live Status Table</h2>
                <button
                    className="collapse-btn"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                    {isCollapsed ? '▼' : '▲'}
                </button>
            </div>

            {!isCollapsed && (
                <>
                    {/* Filters Section */}
                    <div className="filter-section">
                        <div className="search-box">
                            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <select
                            value={countryFilter}
                            onChange={handleCountryFilterChange}
                            className="filter-dropdown"
                        >
                            <option value="">Select Country [All]</option>
                            {countries.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>

                        <select
                            value={stateFilter}
                            onChange={handleStateFilterChange}
                            className="filter-dropdown"
                        >
                            <option value="">Select State [All]</option>
                            {states.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>

                        <select
                            value={zoneFilter}
                            onChange={handleZoneFilterChange}
                            className="filter-dropdown"
                        >
                            <option value="">Select Site [All]</option>
                            {zones.map(zone => (
                                <option key={zone} value={zone}>{zone}</option>
                            ))}
                        </select>

                        <select
                            value={wardFilter}
                            onChange={(e) => setWardFilter(e.target.value)}
                            className="filter-dropdown"
                        >
                            <option value="">Select Location [All]</option>
                            {wards.map(ward => (
                                <option key={ward} value={ward}>{ward}</option>
                            ))}
                        </select>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-dropdown"
                        >
                            <option value="">Select Status [All]</option>
                            {statuses.map(statusVal => (
                                <option key={statusVal} value={statusVal}>{statusVal}</option>
                            ))}
                        </select>

                        {/* <select
                            value={aqiRangeFilter}
                            onChange={(e) => setAqiRangeFilter(e.target.value)}
                            className="filter-dropdown"
                        >
                            <option value="">AQI Range</option>
                            <option value="0-50">0-50 (Good)</option>
                            <option value="51-100">51-100 (Moderate)</option>
                            <option value="101-150">101-150 (USG)</option>
                            <option value="151-200">151-200 (Unhealthy)</option>
                            <option value="201+">201+ (Very Unhealthy)</option>
                        </select> */}
                    </div>

                    {/* Table */}
                    <div className="table-wrapper">
                        <table className="poles-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('id')} className="sortable">
                                        <div className="th-content">
                                            Pole ID
                                            <img src={poleIcon} alt="" className={`sort-icon ${sortConfig.key === 'id' ? sortConfig.direction : ''}`} />
                                        </div>
                                    </th>
                                    <th>Country </th>
                                    <th>State </th>
                                    <th>Site </th>
                                    <th>Location </th>
                                    <th>Temperature </th>
                                    <th>Humidity % </th>
                                    <th onClick={() => handleSort('totalAirCleaned')} className="sortable">
                                        <div className="th-content">
                                            {/* AirPulse™ */}
                                            Total Air Cleaned<br />(cu ft) – Last 24 hrs
                                            <img src={poleIcon} alt="" className={`sort-icon ${sortConfig.key === 'totalAirCleaned' ? sortConfig.direction : ''}`} />
                                        </div>
                                    </th>
                                    {/* <th>PM₂.₅</th>
                                    <th>PM₁₀</th>
                                    <th>CO₂</th> */}

                                    {/* <th onClick={() => handleSort('reduction')} className="sortable">
                                        <div className="th-content">
                                             AirPulse™ Reduction % 
                                           
                                            <img src={poleIcon} alt="" className={`sort-icon ${sortConfig.key === 'reduction' ? sortConfig.direction : ''}`} />
                                        </div>
                                    </th> */}
                                    <th>Status</th>
                                    {/* <th>Description</th> */}

                                    {/* <th>Last Updated</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <tr key={`skeleton-${i}`}>
                                            <td className="pole-id-cell">
                                                <span className="value-skeleton" style={{ width: '60px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '80px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '80px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '100px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '100px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '40px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '40px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '70px', height: '16px' }} />
                                            </td>
                                            <td>
                                                <span className="value-skeleton" style={{ width: '60px', height: '22px', borderRadius: '4px' }} />
                                            </td>
                                        </tr>
                                    ))
                                ) : paginatedPoles.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#8a99ad', fontSize: '14px' }}>
                                            No poles found matching criteria
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedPoles.map((pole, index) => {
                                        return (
                                            <tr key={`${pole.id}-${index}`}>
                                                <td className="pole-id-cell">{pole.id}</td>
                                                <td>{pole.country} </td>
                                                <td>{pole.state} </td>
                                                <td>{pole.zone} </td>
                                                <td>{pole.ward}</td>
                                                <td>{pole.temperature_c != null ? `${Math.round(pole.temperature_c)}°C` : (pole.aqiAfter?.temperature != null ? `${Math.round(pole.aqiAfter.temperature)}°C` : '--')}</td>
                                                <td>{pole.humidity_percent != null ? `${Math.round(pole.humidity_percent)}%` : (pole.aqiBefore?.humidity != null ? `${Math.round(pole.aqiBefore.humidity)}%` : '--')}</td>
                                                <td>{Math.round(pole.dailyVolumeCubicFeet)}</td>
                                                <td>
                                                    <span className={`status-badge bgcolor-${pole.poleStatusId} ${pole.status.toLowerCase()}`}>
                                                        {pole.status_text}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="pagination-controls">
                        <div className="pagination-info">
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedPoles.length)} of {sortedPoles.length} entries
                        </div>
                        <div className="pagination-buttons">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="page-btn"
                            >
                                &lt;&lt;
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="page-btn"
                            >
                                &lt;
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="page-btn"
                            >
                                &gt;
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className="page-btn"
                            >
                                &gt;&gt;
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PolesTable;
